import { fetchAuthSession } from "aws-amplify/auth";
import axios, { AxiosRequestConfig } from "axios";
import { Delay, WithTimeout } from "./AsyncHelpers";

// Requests the user is actively waiting on need to fail fast so they get an error
// instead of an endless spinner. Background loads can afford to wait longer.
export const LOGIN_TIMEOUT_MS = 8000
const DEFAULT_TIMEOUT_MS = 20000
const AUTH_SESSION_TIMEOUT_MS = 8000
const RETRY_DELAY_MS = 300

export interface FetchOptions {
	timeoutMs?: number
	// Callers that show their own retry feedback turn this off and retry themselves.
	retryOnNetworkError?: boolean
}

export type FetchErrorKind = 'network' | 'timeout' | 'auth' | 'server' | 'unknown'

export class FetchError extends Error {
	readonly kind: FetchErrorKind
	readonly status?: number

	constructor(kind: FetchErrorKind, message: string, status?: number) {
		super(message)
		this.name = 'FetchError'
		this.kind = kind
		this.status = status
	}
}

// Checks the name instead of using instanceof, which is unreliable once the build
// transpiles classes down for older browsers.
export function IsFetchError(error: unknown): error is FetchError {
	return (error as FetchError)?.name === 'FetchError'
}

// True when the request never got an answer, so trying again could actually help.
export function IsConnectionProblem(error: unknown): boolean {
	return IsFetchError(error) && (error.kind === 'network' || error.kind === 'timeout')
}

function ToFetchError(error: unknown, verb: string): FetchError {
	if (IsFetchError(error)) {
		return error
	}
	if (axios.isAxiosError(error)) {
		if (error.response) {
			const status = error.response.status
			const kind: FetchErrorKind = (status === 401 || status === 403) ? 'auth' : 'server'
			return new FetchError(kind, `La solicitud ${verb} respondió ${status}`, status)
		}
		if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
			return new FetchError('timeout', `La solicitud ${verb} agotó el tiempo de espera`)
		}
		return new FetchError('network', `La solicitud ${verb} no pudo conectarse`)
	}
	return new FetchError('unknown', `Error en la solicitud ${verb}`)
}

// Only retried for GET requests. A dropped connection usually succeeds on a second
// attempt, but if the server answered we would just make the user wait twice.
function IsRetryableNetworkError(error: unknown): boolean {
	if (!axios.isAxiosError(error) || error.response) {
		return false
	}
	return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.code === 'ERR_NETWORK'
}

async function BuildRequestConfig(sendsJsonBody: boolean, options?: FetchOptions): Promise<AxiosRequestConfig> {
	// Amplify hits the network here whenever the access token needs refreshing.
	let jwt
	try {
		jwt = await WithTimeout(GetJWTForFetchFunction(), AUTH_SESSION_TIMEOUT_MS, 'fetchAuthSession')
	} catch (error) {
		throw new FetchError('timeout', 'No pudimos verificar tu sesión a tiempo')
	}
	if (!jwt) {
		throw new FetchError('auth', 'No hay una sesión válida')
	}
	const headers: Record<string, string> = {
		Authorization: `Bearer ${jwt}`,
	}
	if (sendsJsonBody) {
		headers['Content-Type'] = 'application/json'
	}
	return {
		headers: headers,
		timeout: options?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
	}
}

export async function FetchGetFunction(url: string, options?: FetchOptions): Promise<any> {
	try {
		const getConfig = await BuildRequestConfig(false, options)
		try {
			const response = await axios.get(url, getConfig);
			return response.data
		} catch (error) {
			if (options?.retryOnNetworkError === false || !IsRetryableNetworkError(error)) {
				throw error
			}
			console.warn('Reintentando GET tras un fallo de red:', url)
			await Delay(RETRY_DELAY_MS)
			const retriedResponse = await axios.get(url, getConfig);
			return retriedResponse.data
		}
	} catch (error) {
		console.error('Error al realizar la solicitud GET:', url, error);
		throw ToFetchError(error, 'GET');
	}
}

export async function FetchPostFunction(url: string, data: any, options?: FetchOptions): Promise<any> {
	try {
		const postConfig = await BuildRequestConfig(true, options)
		const response = await axios.post(url, data, postConfig);
		return response.data
	} catch (error) {
		console.error('Error al realizar la solicitud POST:', url, error);
		throw ToFetchError(error, 'POST');
	}
}


export async function FetchPutFunction(url: string, data: any, options?: FetchOptions): Promise<any> {
	try {
		const putConfig = await BuildRequestConfig(true, options)
		const response = await axios.put(url, data, putConfig);
		return response.data
	} catch (error) {
		console.error('Error al realizar la solicitud PUT:', url, error);
		throw ToFetchError(error, 'PUT');
	}
}

export async function FetchDeleteFunction(url: string, options?: FetchOptions): Promise<any> {
	try {
		const deleteConfig = await BuildRequestConfig(false, options)
		const response = await axios.delete(url, deleteConfig);
		return response.data;
	} catch (error) {
		console.error('Error al realizar la solicitud DELETE:', url, error);
		throw ToFetchError(error, 'DELETE');
	}
}




export async function GetJWTForFetchFunction() {
	const session = await fetchAuthSession();
	return session.tokens?.accessToken
}
